import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useAuth, ROLE_QUALITY_MANAGER, ROLE_ADMIN } from '../contexts/AuthContext';
import './SignIn.css';

const firebaseErrorMessage = (err) => {
  switch (err?.code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return 'Invalid email or password.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    case 'auth/unauthorized-email':
    case 'auth/role-mismatch':
      return err.message;
    default:
      return err?.message || 'Login failed. Please check your credentials.';
  }
};

const SignIn = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    userType: ROLE_QUALITY_MANAGER,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await login(formData.email.trim(), formData.password, formData.userType);
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      setError(firebaseErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="signin-container">
      <Container fluid className="h-100">
        <Row className="h-100">
          {/* Left Section - Lottie Animation */}
          <Col lg={8} className="logo-section d-flex">
            <div className="logo-content">
              <div className="vitrag-logo-container">
                <img
                  src="/logo.png"
                  alt="Vitrag Associates"
                  className="vitrag-logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>

              <DotLottieReact
                src="https://lottie.host/f9426c8a-f34e-409c-811a-0501266eb23a/b2oojgjcQn.lottie"
                loop
                autoplay
                className="lottie-animation"
              />
            </div>
          </Col>

          {/* Right Section - Login Form */}
          <Col lg={4} className="form-section d-flex align-items-center">
            <div className="form-container">
              <div className="welcome-section mb-4">
                <h2 className="welcome-title">
                  Welcome to{' '}
                  <span className="company-name">
                    <span className="vitrag-text">VITRAG</span> ASSOCIATES LLP - TESTING LAB
                  </span>
                </h2>
              </div>

              <Card className="login-card">
                <Card.Body className="p-4">
                  {error && (
                    <Alert variant="danger" className="mb-3">
                      <i className="fas fa-exclamation-circle me-2"></i>
                      {error}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <div className="user-type-selection mb-4">
                      <div className="d-flex gap-4">
                        <Form.Check
                          type="radio"
                          id="quality-manager"
                          name="userType"
                          value={ROLE_QUALITY_MANAGER}
                          label="Quality Manager"
                          checked={formData.userType === ROLE_QUALITY_MANAGER}
                          onChange={handleInputChange}
                          className="user-type-radio"
                        />
                        <Form.Check
                          type="radio"
                          id="admin"
                          name="userType"
                          value={ROLE_ADMIN}
                          label="Admin"
                          checked={formData.userType === ROLE_ADMIN}
                          onChange={handleInputChange}
                          className="user-type-radio"
                        />
                      </div>
                    </div>

                    <Form.Group className="mb-3">
                      <Form.Label className="form-label">EMAIL</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter your email"
                        className="form-input"
                        autoComplete="username"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="form-label">PASSWORD</Form.Label>
                      <div className="password-input-container">
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Enter your password"
                          className="form-input password-input"
                          autoComplete="current-password"
                          required
                        />
                        <Button
                          type="button"
                          variant="link"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </Button>
                      </div>
                    </Form.Group>

                    <Button type="submit" className="signin-button w-100" disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Signing In...
                        </>
                      ) : (
                        'Sign In'
                      )}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SignIn;
